import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { DecimalPipe } from '@angular/common';

/**
 * KPI tile: a label, a headline value with unit and an optional caption. Used for metric
 * summaries (power now, energy, peak; later occupancy and usage).
 */
@Component({
    selector: 'app-metric-tile',
    standalone: true,
    imports: [DecimalPipe],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <div class="metric-tile" [attr.aria-label]="label() + ': ' + (text() || (hasValue() ? value() + ' ' + unit() : '–'))">
            <span class="metric-tile__label">
                @if (icon()) {
                    <i [class]="icon()" aria-hidden="true"></i>
                }
                {{ label() }}
            </span>
            <span class="metric-tile__value">
                @if (text()) {
                    <span class="metric-tile__text">{{ text() }}</span>
                } @else if (hasValue()) {
                    {{ value() | number: format() }}<span class="metric-tile__unit">{{ unit() }}</span>
                } @else {
                    –
                }
            </span>
            @if (caption()) {
                <span class="metric-tile__caption">{{ caption() }}</span>
            }
        </div>
    `,
    styles: [
        `
            :host {
                display: block;
                min-width: 0;
            }
            .metric-tile {
                display: flex;
                flex-direction: column;
                gap: 0.25rem;
                padding: 0.875rem 1rem;
                border: 1px solid var(--surface-border);
                border-radius: var(--app-card-radius);
                background: var(--surface-card);
                height: 100%;
            }
            .metric-tile__label {
                font-size: 0.8125rem;
                color: var(--text-color-secondary);
                display: flex;
                align-items: center;
                gap: 0.375rem;
            }
            .metric-tile__value {
                font-size: 1.5rem;
                font-weight: 600;
                line-height: 1.2;
                color: var(--text-color);
                font-variant-numeric: tabular-nums;
            }
            .metric-tile__unit {
                font-size: 0.875rem;
                font-weight: 500;
                margin-left: 0.25rem;
                color: var(--text-color-secondary);
            }
            .metric-tile__text {
                font-size: 1.125rem;
                line-height: 1.6;
                overflow-wrap: anywhere;
            }
            .metric-tile__caption {
                font-size: 0.75rem;
                color: var(--text-color-secondary);
            }
        `
    ]
})
export class MetricTileComponent {
    readonly label = input.required<string>();
    readonly value = input<number | null | undefined>(null);
    readonly unit = input<string>('');
    readonly icon = input<string | null>(null);
    readonly caption = input<string | null>(null);
    /** Text shown instead of a number, for states such as "Occupied". */
    readonly text = input<string | null>(null);
    /** DecimalPipe digits format. */
    readonly format = input<string>('1.0-1');

    readonly hasValue = computed(() => {
        const v = this.value();
        return v != null && Number.isFinite(v);
    });
}
